import React, { useEffect } from 'react'
import { Sprite, userAgent } from '@theneatcompany/nui'
import Script from 'next/script'
import PropTypes from 'prop-types'
import { useRouter } from 'next/router'
import { useSiteConfig } from '@paper-monster/web/src/components/Providers/SiteConfigProvider'
import { useNonce } from '@paper-monster/web/src/utils/nonce'

import Providers from '@/components/Providers/Providers'
import routes from '@/routes'
import styles from '@/components/layout/Layout.module.scss'

import { Layout } from './layout'

const isTouchDevice = () => (('ontouchstart' in window)
  || navigator.maxTouchPoints > 0
  || navigator.msMaxTouchPoints > 0)

// Each tag renders only when the site config sets its ID.
const App = ({ children, layoutProps }) => {
  const { pathname } = useRouter()
  const { tracking } = useSiteConfig()
  const { gtmId, buyFlowGtmId, uetTagId, facebookPixelId } = tracking

  const isInBuyFlow = [routes.signUp, routes.signUpSuccess].includes(pathname)
  const nonce = useNonce()

  useEffect(() => {
    const body = document.querySelector('body')
    if (!body) return

    if (userAgent.isMobile) {
      body.classList.add('mobile-device')
    }
    if (isTouchDevice()) {
      body.classList.add('touch-device')
    }
  }, [])

  return (
    <>
      <div className={styles.app} id="app">
        <Providers>
          <Layout {...layoutProps}>
            {children}
          </Layout>
        </Providers>
      </div>
      {/* Sprite is necessary in order to render the svgs for nui icons */}
      <Sprite />
      {gtmId && (
        <Script id="google-tag-manager" nonce={nonce} strategy="afterInteractive">
          {`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;var n=d.querySelector('[nonce]');
            n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtmId}');
          `}
        </Script>
      )}
      {uetTagId && (
        <Script id="microsoft-ads-uet" nonce={nonce} strategy="afterInteractive">
          {`
            (function(w, d, t, r, u) {
                var f, n, i;
                w[u] = w[u] || [], f = function() {
                    var o = {
                        ti: "${uetTagId}",
                        enableAutoSpaTracking: true
                    };
                    o.q = w[u], w[u] = new UET(o), w[u].push("pageLoad")
                }, n = d.createElement(t), n.src = r, n.async = 1, n.onload = n.onreadystatechange = function() {
                    var s = this.readyState;
                    s && s !== "loaded" && s !== "complete" || (f(), n.onload = n.onreadystatechange = null)
                }, i = d.getElementsByTagName(t)[0], i.parentNode.insertBefore(n, i)
            })(window, document, "script", "//bat.bing.com/bat.js", "uetq");
          `}
        </Script>
      )}
      {isInBuyFlow && (
        <>
          {buyFlowGtmId && (
            <Script id={buyFlowGtmId} nonce={nonce} strategy="afterInteractive">
              {`
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;var n=d.querySelector('[nonce]');
                n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${buyFlowGtmId}');
              `}
            </Script>
          )}
          {facebookPixelId && (
            <Script id="facebook-pixel" nonce={nonce} strategy="afterInteractive">
              {`
                !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '${facebookPixelId}');
                fbq('track', 'PageView');
              `}
            </Script>
          )}
        </>
      )}
    </>
  )
}

App.propTypes = {
  children: PropTypes.node.isRequired,
  layoutProps: PropTypes.shape({}),
}

export default App
